import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import _ from 'lodash';
import {
    Table,
    TableBody,
    TableContainer, TableRow,
} from '@mui/material';

import { companiesSummaryTableHead } from '../app/config/tables/companiesTableHead';
import { CompaniesTableFilter, CompaniesTableRow, TableHeader, TablePagination } from '../components/table';
import useCompanies from '../hooks/useCompanies';
import {useLocation, useParams, useSearchParams} from "react-router-dom";


const toUrlObj = (f) => {
    const o = {};
    if (f.pageSize) o.pageSize = String(f.pageSize);
    if (f.pageNumber) o.pageNumber = String(f.pageNumber);
    if (f.orderBy) o.orderBy = f.orderBy;
    if (f.orderDirection) o.orderDirection = f.orderDirection;
    if (f.search) o.search = f.search;
    return o;
};

const fromUrl = (sp) => {
    const num = (v, d) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : d;
    };
    const dir = (v) => (v === "asc" || v === "desc" ? v : null);
    return {
        pageSize:       num(sp.get("pageSize"), 6),
        pageNumber:     num(sp.get("pageNumber"), 1),
        orderBy:        sp.get("orderBy") || null,
        orderDirection: dir(sp.get("orderDirection")),
        search: sp.get("search"),
    };
};

const sameParams = (a, b) => {
    const A = Object.entries(a).filter(([, v]) => v !== null && v !== undefined && v !== "");
    const B = Object.entries(b).filter(([, v]) => v !== null && v !== undefined && v !== "");
    if (A.length !== B.length) return false;
    return A.every(([k, v]) => String(v) === String(b[k]));
};


const CompaniesScreen = () => {
    const [open, setOpen] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const initial = useMemo(() => fromUrl(searchParams), []);


    const {
        data: companies,
        error,
        filter,
        setFilter,
        nextPage,
        previousPage,
        refresh,
        pagination,
    } = useCompanies(initial);

    useEffect(() => {
        const incoming = fromUrl(searchParams);
        const current  = {
            pageSize: filter.pageSize,
            pageNumber: filter.pageNumber,
            orderBy: filter.orderBy,
            orderDirection: filter.orderDirection,
            search: filter.search,
        };

        if (!location.search) {
            setSelectedItems([]);
            setOpen(null);
            return;
        }
        
        if (!sameParams(incoming, current)) {
            Object.entries(incoming).forEach(([k, v]) => setFilter(k, v));
        }
    }, [searchParams]);

    useEffect(() => {
        const desired = toUrlObj(filter);
        const current = Object.fromEntries(searchParams.entries());
        if (!sameParams(desired, current)) {
            setSearchParams(desired, { replace: true });
        }
    }, [
        filter.pageSize,
        filter.pageNumber,
        filter.orderBy,
        filter.orderDirection,
        filter.search,
        searchParams,
        setSearchParams
    ]);

    const isChecked = useCallback(
        (companyId) => selectedItems.some((i) => i.id === companyId),
        [selectedItems]
    );

    const handleRowCheck = useCallback(
        (company, checkedNow) => {
            setSelectedItems((prev) => {
                if (!checkedNow) {
                    if (prev.some((p) => p.id === company.id)) return prev;
                    return [...prev, company];
                }
                
                return prev.filter((p) => p.id !== company.id);
            });
        }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedItems((prev) => {
            if (prev.length === companies.length) return [];
            return companies;
        });
    }, [companies]);

    const showOnPage = useCallback(
        (value) => {
            setFilter('pageSize', value);
            setFilter('pageNumber', 1);
        }, []);

    const handleRequestSort = useCallback(
        (event, property) => {
            const direction =
                filter.orderBy === property && filter.orderDirection === 'asc'
                    ? 'desc'
                    : 'asc';

            setFilter('orderBy', property);
            setFilter('orderDirection', direction);
        },
        [filter.orderBy, filter.orderDirection]
    );

    const handleRowClick = useCallback(
        (id) => {
            setOpen((curr) => (curr === id ? null : id));
        }, []);

    const rowCount = useMemo(() => companies.length, [companies]);
    const numSelected = useMemo(() => selectedItems.length, [selectedItems]);

    const onMergeCompleted = () => {
        setSelectedItems([]);
        refresh();
    }

    const beforeUrl = `${location.pathname}${location.search}`;
    
    return (
        <div className="fk-screen">
            {error && (
                <div style={{ padding: 8, color: 'crimson' }}>Error: {error}</div>
            )}
            <CompaniesTableFilter 
                filter={filter} 
                setFilter={setFilter}
                onSubmit={refresh}
                selectedItems={selectedItems}
                onMergeCompleted={onMergeCompleted}
            />

            <TableContainer>
                <Table size="medium" className="fk-table">
                    <TableHeader
                        crud
                        withCheckbox
                        items={companiesSummaryTableHead}
                        orderBy={filter.orderBy}
                        order={filter.orderDirection}
                        numSelected={numSelected}
                        rowCount={rowCount}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAll}
                        screen="CompaniesScreen"
                    />
                    <TableBody>
                        {companies.map((company) => {
                            const checked = isChecked(company.id);
                            return (
                                <CompaniesTableRow
                                    key={company.id}
                                    checked={checked}
                                    company={company}
                                    open={open === company.id}
                                    onRowClick={handleRowClick}
                                    onCheck={() => handleRowCheck(company, checked)}
                                    before={beforeUrl}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                filter={pagination}
                handlePreviousPage={previousPage}
                handleNextPage={nextPage}
                showOnPage={showOnPage}
            />
        </div>
    );
};

export default CompaniesScreen;
